import { useCallback, useEffect, useMemo, useState } from 'react';

import { type AdminAgentChatPanelSession } from '@apps/web/components/admin/admin_agent_chat_panel';
import {
    type AdminAgentChatSession,
    type AdminAgentChatSessionChrome,
    type AdminAgentChatSessionOptions,
    type AdminAgentInitialPageContext,
} from '@apps/web/components/admin/admin_agent_chat_session_types';
import { resolveAdminAgentMessageDeliveryMode } from '@apps/web/components/admin/admin_agent_delivery_mode';
import { useAdminAgentChatStream } from '@apps/web/components/admin/use_admin_agent_chat_stream';
import {
    type AdminAgentSubmitMessageOptions,
    useAdminAgentRunActions,
} from '@apps/web/components/admin/use_admin_agent_run_actions';
import { useAdminAgentToolActions } from '@apps/web/components/admin/use_admin_agent_tool_actions';
import { type ChatInputSubmitMetadata } from '@apps/web/components/phoebe_chat/chat_input';
import { RUN_METADATA_REFRESH_MS } from '@apps/web/routes/_app/admin/agent/admin_agent_helpers';

export { type AdminAgentInitialPageContext };

const DEFAULT_CHROME: AdminAgentChatSessionChrome = {
    embedded: false,
    showFullPageChrome: true,
    showHistory: true,
};

export function useAdminAgentChatSession(
    options: AdminAgentChatSessionOptions,
): AdminAgentChatSession {
    const {
        canUseAdminAgent,
        chrome: chromeOptions,
        initialPageContext = null,
        isSelectedRunArchived,
        mutateRun,
        refreshRunLists,
        selectedOrganizationApprovalName,
        selectedOrganizationId,
        selectedRun,
        selectedRunId,
        selectedRunStatus,
        streamOrganizationId,
    } = options;
    const [draftRunId, setDraftRunId] = useState<string | null>(null);
    const allowedSelectedRunId = selectedRunId ?? draftRunId;
    const canSendMessage =
        canUseAdminAgent && !!selectedOrganizationId && !isSelectedRunArchived;
    const [isSending, setIsSending] = useState(false);
    const chrome = useMemo(
        () => ({
            ...DEFAULT_CHROME,
            ...chromeOptions,
        }),
        [chromeOptions],
    );

    useEffect(() => {
        if (!selectedRunId || draftRunId === null) {
            return;
        }
        setDraftRunId(null);
    }, [draftRunId, selectedRunId]);

    const runActionOptions = useMemo<AdminAgentChatSessionOptions>(
        () => ({
            ...options,
            onRunCreated: async (runId) => {
                setDraftRunId(runId);
                try {
                    await options.onRunCreated?.(runId);
                } catch {
                    // Caller-owned selection persistence is best-effort; the
                    // draft run is already available for the embedded session.
                }
            },
        }),
        [options],
    );

    const chatStream = useAdminAgentChatStream({
        allowedSelectedRunId,
        isSending,
        selectedOrganizationId,
        selectedRun,
        selectedRunStatus,
        streamOrganizationId,
    });
    const streamErrorMessage =
        allowedSelectedRunId && chatStream.runEventStream.error
            ? getAdminAgentStreamErrorMessage({
                  connectionStatus: chatStream.runEventStream.connectionStatus,
                  error: chatStream.runEventStream.error,
              })
            : null;
    const runActions = useAdminAgentRunActions({
        initialPageContext,
        isSending,
        options: runActionOptions,
        removeOptimisticPendingEvent: chatStream.removeOptimisticPendingEvent,
        refreshRunEventStream: chatStream.runEventStream.refresh,
        setIsSending,
        stageOptimisticUserMessage: chatStream.stageOptimisticUserMessage,
    });
    const toolActions = useAdminAgentToolActions({
        allowedSelectedRunId,
        // Match the create/message/tier path so interrupt +
        // tool-decision requests carry the same X-Mode as the run.
        currentMode: options.currentMode,
        mutateRun,
        runEventStream: chatStream.runEventStream,
    });
    const submitMessage = useCallback(
        (
            content: string,
            submitMetadata?: ChatInputSubmitMetadata,
            submitOptions?: AdminAgentSubmitMessageOptions,
        ): Promise<boolean | void> =>
            runActions.submitMessage(
                content,
                resolveAdminAgentMessageDeliveryMode({
                    isSelectedRunBusy: chatStream.isSelectedRunBusy,
                    submitMetadata,
                }),
                submitOptions,
            ),
        [chatStream.isSelectedRunBusy, runActions],
    );
    const selectedTier = selectedRun?.selected_tier ?? runActions.draftTier;
    const selectedResolvedTier = selectedRun?.resolved_tier ?? null;
    const selectedTierResolutionReason =
        selectedRun?.tier_resolution_reason ?? null;
    const selectedModel = selectedRun
        ? (selectedRun.active_model ?? null)
        : runActions.draftModel;
    const selectedResolvedModel =
        selectedRun?.active_model ?? selectedRun?.tier_model ?? null;
    const { resetDraftRun: resetPendingDraftRun } = runActions;
    const shouldRefreshRunMetadata =
        !!selectedRunId && chatStream.chatState.shouldRefreshRunMetadata;

    const refreshRunMetadata = useCallback(() => {
        refreshRunLists();
        void mutateRun();
    }, [mutateRun, refreshRunLists]);

    const resetDraftRun = useCallback(() => {
        setDraftRunId(null);
        resetPendingDraftRun();
    }, [resetPendingDraftRun]);

    useEffect(() => {
        if (!shouldRefreshRunMetadata) {
            return;
        }

        const intervalId = window.setInterval(() => {
            refreshRunMetadata();
        }, RUN_METADATA_REFRESH_MS);

        return () => window.clearInterval(intervalId);
    }, [refreshRunMetadata, shouldRefreshRunMetadata]);

    const panelSession = useMemo<AdminAgentChatPanelSession>(
        () => ({
            allowedSelectedRunId,
            canSendMessage,
            chatState: chatStream.chatState,
            decidingCallIds: toolActions.decidingCallIds,
            errorMessage: runActions.errorMessage ?? streamErrorMessage,
            imageAttachments: runActions.imageAttachments,
            imageUploadDisabled: runActions.imageUploadDisabled,
            inspectedToolEntryId: toolActions.inspectedToolEntryId,
            isCreatingRun: runActions.isCreatingRun,
            isInterrupting: toolActions.isInterrupting,
            isSelectedRunBusy: chatStream.isSelectedRunBusy,
            isSending: runActions.isSending,
            renderMessageArtifacts: () => null,
            renderToolArtifacts: () => null,
            renderToolOutput: () => null,
            selectedOrganizationApprovalName,
            selectedOrganizationId,
            submitMessage,
            transientProgress: chatStream.transientProgress,
            onImageFilesSelected: runActions.uploadImageFiles,
            onRemoveImageAttachment: runActions.removeImageAttachment,
            decideToolCall: toolActions.decideToolCall,
            interruptSelectedRun: toolActions.interruptSelectedRun,
            setInspectedToolEntryId: toolActions.setInspectedToolEntryId,
        }),
        [
            allowedSelectedRunId,
            canSendMessage,
            chatStream.chatState,
            chatStream.isSelectedRunBusy,
            runActions.errorMessage,
            runActions.imageAttachments,
            runActions.imageUploadDisabled,
            runActions.isCreatingRun,
            runActions.isSending,
            runActions.removeImageAttachment,
            runActions.uploadImageFiles,
            selectedOrganizationApprovalName,
            selectedOrganizationId,
            streamErrorMessage,
            submitMessage,
            chatStream.transientProgress,
            toolActions.decideToolCall,
            toolActions.decidingCallIds,
            toolActions.inspectedToolEntryId,
            toolActions.interruptSelectedRun,
            toolActions.isInterrupting,
            toolActions.setInspectedToolEntryId,
        ],
    );

    return {
        ...panelSession,
        chrome,
        createRun: runActions.createRun,
        draftModel: runActions.draftModel,
        draftTier: runActions.draftTier,
        isSelectedRunBusy: chatStream.isSelectedRunBusy,
        isUpdatingModel: runActions.isUpdatingModel,
        isUpdatingTier: runActions.isUpdatingTier,
        pendingRunEvents: chatStream.pendingEvents,
        runEventStream: chatStream.runEventStream,
        selectedModel,
        selectedResolvedModel,
        selectedResolvedTier,
        selectedTier,
        selectedTierResolutionReason,
        clearErrorMessage: runActions.clearErrorMessage,
        refreshRunMetadata,
        resetDraftRun,
        updateSelectedModel: runActions.updateSelectedModel,
        updateSelectedTier: runActions.updateSelectedTier,
    };
}

function getAdminAgentStreamErrorMessage({
    connectionStatus,
    error,
}: {
    readonly connectionStatus: string;
    readonly error: Error;
}): string {
    if (connectionStatus === 'not_found') {
        return 'The Admin Agent run could not be found.';
    }
    return `The Admin Agent stream disconnected: ${error.message}`;
}
